import React, { useEffect, useState } from 'react';
import { LuPencil } from 'react-icons/lu';

import { AccountGroup, AccountGroupAdvisorUpdateRequest } from '@ndvr/portfolio-api';

import { Button } from '@/components/ui/button';
import { formatPercent, useRequiredParams } from 'utils';
import { useAccountGroupProfileWithAumIncluded } from 'views/portfolio/account-aum-queries';
import { useUpdateAccountGroupAsAdvisor } from 'views/portfolio/account-group-queries';

export const WithdrawalTaxRateOverride = () => {
    const { accountGroupGUID } = useRequiredParams(['accountGroupGUID']);
    const { data: accountGroupProfile } = useAccountGroupProfileWithAumIncluded(accountGroupGUID);
    const accountGroup = accountGroupProfile?.accountGroup;

    const { mutate: updateAccountGroupAsAdvisor, isPending: isUpdating } =
        useUpdateAccountGroupAsAdvisor(accountGroupGUID);

    const [editing, setEditing] = useState(false);
    const [editableAccountGroup, setEditableAccountGroup] = useState<Partial<AccountGroup> | undefined>(undefined);
    const [percentageDisplay, setPercentageDisplay] = useState('');

    useEffect(() => {
        if (accountGroup) {
            setEditableAccountGroup(accountGroup);
            setPercentageDisplay(
                accountGroup.withdrawalTaxRate !== undefined
                    ? (parseFloat(accountGroup.withdrawalTaxRate) * 100).toFixed(1)
                    : '',
            );
        }
    }, [accountGroup]);

    const handleEdit = () => {
        setEditing(true);
    };

    const handleCancel = () => {
        setEditableAccountGroup(accountGroup);
        setPercentageDisplay(
            accountGroup?.withdrawalTaxRate !== undefined
                ? (parseFloat(accountGroup.withdrawalTaxRate) * 100).toFixed(1)
                : '',
        );
        setEditing(false);
    };

    const handleSave = () => {
        if (editableAccountGroup?.withdrawalTaxRate !== accountGroup?.withdrawalTaxRate) {
            const updateRequest: AccountGroupAdvisorUpdateRequest = {
                ...accountGroup!,
                withdrawalTaxRate: editableAccountGroup?.withdrawalTaxRate,
            };
            updateAccountGroupAsAdvisor(updateRequest, {
                onSuccess: () => {
                    setEditing(false);
                },
            });
        } else {
            setEditing(false);
        }
    };

    const isCustomRateInvalid =
        editableAccountGroup?.withdrawalTaxRate !== undefined &&
        editableAccountGroup?.withdrawalTaxRate !== null &&
        percentageDisplay === '';

    const hasChanges = editableAccountGroup?.withdrawalTaxRate !== accountGroup?.withdrawalTaxRate;

    return (
        <div className="group border-border rounded-lg border bg-white p-4">
            <div className="mb-2 flex items-center justify-between">
                <h3 className="text-cocoa-1000 font-semibold">Withdrawal Tax Rate</h3>
                {!editing && (
                    <Button
                        className="h-8 w-8 opacity-0 transition-opacity group-hover:opacity-100"
                        size="icon"
                        variant="ghost"
                        onClick={handleEdit}
                    >
                        <LuPencil className="h-4 w-4 text-gray-500 hover:text-blue-600" />
                    </Button>
                )}
            </div>
            {editing ? (
                <div className="space-y-3">
                    <label className="flex items-center space-x-2">
                        <input
                            type="radio"
                            checked={
                                editableAccountGroup?.withdrawalTaxRate === undefined ||
                                editableAccountGroup?.withdrawalTaxRate === null
                            }
                            onChange={() => {
                                setEditableAccountGroup((prev) => ({ ...prev, withdrawalTaxRate: undefined }));
                                setPercentageDisplay('');
                            }}
                            className="h-4 w-4"
                        />
                        <span className="text-sm">Set by tax profile</span>
                    </label>

                    <label className="flex items-center space-x-2">
                        <input
                            type="radio"
                            checked={
                                editableAccountGroup?.withdrawalTaxRate !== undefined &&
                                editableAccountGroup?.withdrawalTaxRate !== null
                            }
                            onChange={() => {
                                setEditableAccountGroup((prev) => ({ ...prev, withdrawalTaxRate: '0' }));
                                setPercentageDisplay('0');
                            }}
                            className="h-4 w-4"
                        />
                        <span className="text-sm">Custom rate</span>
                    </label>

                    {editableAccountGroup?.withdrawalTaxRate !== undefined &&
                        editableAccountGroup?.withdrawalTaxRate !== null && (
                            <div className="relative ml-6">
                                <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="100"
                                    value={percentageDisplay}
                                    onChange={(e) => setPercentageDisplay(e.target.value)}
                                    onBlur={(e) => {
                                        const percentValue = e.target.value;
                                        const decimalValue = percentValue
                                            ? (Math.max(parseFloat(percentValue), 0) / 100).toString()
                                            : '0';

                                        setEditableAccountGroup((prev) => ({
                                            ...prev,
                                            withdrawalTaxRate: decimalValue,
                                        }));
                                    }}
                                    className="w-full rounded border border-gray-300 px-3 py-2 pr-8 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                                />
                                <span className="absolute top-1/2 right-3 -translate-y-1/2 transform text-gray-500">
                                    %
                                </span>
                            </div>
                        )}

                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={handleCancel} disabled={isUpdating}>
                            Cancel
                        </Button>
                        <Button
                            variant="default"
                            size="sm"
                            onClick={handleSave}
                            disabled={!hasChanges || isCustomRateInvalid || isUpdating}
                        >
                            {isUpdating ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="text-sm">
                    {accountGroup?.withdrawalTaxRate !== undefined
                        ? formatPercent(parseFloat(accountGroup?.withdrawalTaxRate || '0') * 100) + ' (override)'
                        : 'Set by tax profile'}
                </div>
            )}
        </div>
    );
};
